import { useEffect, useState } from "react";
import { useMapContext } from "../../context/MapContext";
import { useLanguageContext } from "../../context/LanguageContext";
import { filterIcon } from "../../assets/icons";
import { CATEGORY_IDS, SERVICE_TYPES } from "../../constants";

const INITIAL_FILTERS = {
  text: "",
  category: "",
  serviceType: "",
  activity: "",
  onlyDiscount: false,
};

export default function SearchInput({ filtersOpen, setFiltersOpen }) {
  const { t } = useLanguageContext();
  const { markers, setFilteredMarkers } = useMapContext();

  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const toggleFilters = () => setFiltersOpen((prev) => !prev);

  const updateFilter = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;

    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClear = () => {
    setFilters(INITIAL_FILTERS);
  };

  useEffect(() => {
    let filtered = markers;

    if (filters.text) {
      const text = filters.text.toLowerCase();
      filtered = filtered.filter((item) => Object.values(item).join("").toLowerCase().includes(text));
    }

    if (filters.category !== "") {
      filtered = filtered.filter((item) => item.category === filters.category);
    }

    if (filters.serviceType !== "") {
      filtered = filtered.filter((item) => item.serviceType.includes(filters.serviceType));
    }

    if (filters.activity !== "") {
      filtered = filtered.filter((item) => String(item.activity) === filters.activity);
    }

    if (filters.onlyDiscount) {
      filtered = filtered.filter((item) => item.discount);
    }

    setFilteredMarkers(filtered);
  }, [filters, markers, setFilteredMarkers]);

  return (
    <section className={`flex flex-col ${filtersOpen && "h-full"}`}>
      <div className="flex items-center bg-gray-800 border border-gray-700 rounded-lg text-white pr-2 mb-2">
        <input
          type="text"
          value={filters.text}
          onChange={updateFilter("text")}
          placeholder={t("component", "searchInput", "searchInputPlaceholder")}
          className="w-full bg-gray-800 py-3 pl-3 rounded-lg"
        />
        {filters.text && (
          <button
            className="w-8 h-8 rounded-lg hover:text-red-400 duration-300"
            onClick={() => setFilters((prev) => ({ ...prev, text: "" }))}
          >
            ✕
          </button>
        )}
        <button className={`duration-300 hover:text-green-600 ${filtersOpen && "rotate-180"}`} onClick={toggleFilters}>
          {filterIcon}
        </button>
      </div>

      {filtersOpen && (
        <div className="flex flex-col overflow-hidden">
          <div className="overflow-y-auto pr-2 customScrollBar space-y-4">
            <div className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-lg px-3 h-11 mt-2">
              <span className="text-sm text-white">{t("component", "searchInput", "withDiscount")}</span>
              <button
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    onlyDiscount: !prev.onlyDiscount,
                  }))
                }
                className={`relative w-11 h-6 rounded-full transition ${filters.onlyDiscount ? "bg-main_accent" : "bg-gray-600"}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                    filters.onlyDiscount ? "translate-x-5" : ""
                  }`}
                />
              </button>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white text-sm">{t("component", "searchInput", "filterActivity")}</label>
              <select
                value={filters.activity}
                onChange={(e) => setFilters((p) => ({ ...p, activity: e.target.value }))}
                className="bg-gray-800 border border-gray-700 rounded-lg h-11 px-3 text-sm text-white"
              >
                <option value="">{t("component", "modal", "lastActivitySelect")}</option>
                <option value="1">🟢 {t("component", "modal", "lastActivityShort")}</option>
                <option value="2">🟡 {t("component", "modal", "lastActivityMedium")}</option>
                <option value="3">🔴 {t("component", "modal", "lastActivityLong")}</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white text-sm">{t("component", "searchInput", "filterService")}</label>
              <select
                value={filters.serviceType}
                onChange={(e) => setFilters((p) => ({ ...p, serviceType: e.target.value }))}
                className="bg-gray-800 border border-gray-700 rounded-lg h-11 px-3 text-sm text-white"
              >
                <option value="">{t("component", "marker", "allStores")}</option>
                <option value={SERVICE_TYPES.LOCAL}>{t("component", "marker", "localStore")}</option>
                <option value={SERVICE_TYPES.PHYSICAL}>{t("component", "marker", "physicalService")}</option>
                <option value={SERVICE_TYPES.DIGITAL}>{t("component", "marker", "digitalService")}</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-white text-sm">{t("component", "searchInput", "filterCategory")}</label>

              <select
                value={filters.category ?? ""}
                onChange={(e) =>
                  setFilters((p) => ({
                    ...p,
                    category: e.target.value === "" ? "" : Number(e.target.value),
                  }))
                }
                className="bg-gray-800 border border-gray-700 rounded-lg h-11 px-3 text-sm text-white"
              >
                <option value="">{t("component", "categories", "16")}</option>

                {Object.keys(CATEGORY_IDS).map((_, index) => (
                  <option key={index} value={index}>
                    {t("component", "categories", index)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={handleClear}
            className="w-full py-2 px-4 mb-6 mt-3 text-sm rounded-md border border-gray-600 text-gray-300 hover:border-red-500 hover:text-red-400 transition"
          >
            {t("component", "searchInput", "clearFilters")}
          </button>
        </div>
      )}
    </section>
  );
}
